import React, {useCallback, useEffect, useMemo, useState} from 'react';
import classNames from 'classnames';
import {FormCustomInput, SearchIcon} from 'components';
import ComboBoxDateRange from 'components/Basic/ComboBox/ComboBoxDateRange';
import {IndeterminateComboBox} from 'components/Basic/ComboBox/IndeterminateComboBox';
import SingleComboBox from 'components/Basic/ComboBox/SingleComboBox';
import {DateRange} from 'components/Basic/DateRangeV2/types';
import ArrowDownShortIcon from 'components/Basic/SvgIcon/ArrowDownShort';
import {selectUserProfile} from 'features/User';
import {GetSessionsFilters} from 'interfaces';
import {FilterMemberType, FilterPaymentType} from 'interfaces/revenue.type';
import debounce from 'lodash/debounce';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router';

interface FilterSectionProps {
  onFilterChange: (filters: GetSessionsFilters) => void;
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  onFilterChange,
}) => {
  const user = useSelector(selectUserProfile);
  const location = useLocation();
  const isSingleSession = location.pathname.includes('single-sessions');

  const getInitialFilters = useCallback(
    (): GetSessionsFilters => ({
      search: '',
      memberType: '',
      paymentType: '',
      insuranceCompany: [],
      dateRange: {from: null, to: null},
      providerId: user?._id ?? '',
      sessionType: isSingleSession ? 'individual' : 'group',
      sortBy: 'latest',
    }),
    [user?._id, isSingleSession],
  );

  const [filters, setFilters] = useState<GetSessionsFilters>(getInitialFilters);
  const [searchInputValue, setSearchInputValue] = useState('');

  const handleFilterChange = useCallback(
    <K extends keyof GetSessionsFilters>(
      key: K,
      value: GetSessionsFilters[K],
    ) => {
      setFilters(prev => ({...prev, [key]: value}));
    },
    [],
  );

  const handleSortBy = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      sortBy: prev.sortBy === 'latest' ? 'oldest' : 'latest',
    }));
  }, []);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilters(prev => ({...prev, search: value}));
      }, 1000),
    [],
  );

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInputValue(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  const handleDayClick = useCallback(
    (range: DateRange) => {
      handleFilterChange('dateRange', {
        from: range.startDate,
        to: range.endDate,
      });
    },
    [handleFilterChange],
  );

  const handleMemberTypeChange = useCallback(
    (option: {value: string} | null) => {
      handleFilterChange('memberType', option?.value ?? '');
    },
    [handleFilterChange],
  );

  const handlePaymentTypeChange = useCallback(
    (option: {value: string} | null) => {
      handleFilterChange('paymentType', option?.value ?? '');
    },
    [handleFilterChange],
  );

  const handleInsuranceCompanyChange = useCallback(
    (option: {value: string}[]) => {
      handleFilterChange(
        'insuranceCompany',
        option.map(o => o.value),
      );
    },
    [handleFilterChange],
  );

  const handleClearFilters = useCallback(() => {
    const clearedFilters = getInitialFilters();
    setFilters(clearedFilters);
    setSearchInputValue('');
    debouncedSetSearch.cancel();
  }, [getInitialFilters, debouncedSetSearch]);

  useEffect(() => {
    onFilterChange(filters);
  }, [filters, onFilterChange]);

  const hasActiveFilters = useMemo(() => {
    const initialFilters = getInitialFilters();
    return (
      filters.memberType !== initialFilters.memberType ||
      filters.paymentType !== initialFilters.paymentType ||
      filters.insuranceCompany.length > 0 ||
      filters.dateRange.from !== null ||
      filters.dateRange.to !== null ||
      filters.search !== initialFilters.search ||
      filters.sortBy !== initialFilters.sortBy
    );
  }, [filters, getInitialFilters]);

  const {t} = useTranslation();

  const memberTypes = [
    {
      value: '',
      label: t('singleSession.all_clients', 'All Clients'),
    },
    {
      value: FilterMemberType.WELLNITE_MEMBERS,
      label: t('singleSession.wellnite_members', 'Wellnite Members'),
    },
    {
      value: FilterMemberType.DP_CLIENTS,
      label: t('singleSession.dp_clients', 'Digital Practice Clients'),
    },
  ];

  const paymentTypes = [
    {value: '', label: t('singleSession.all', 'All')},
    {
      value: FilterPaymentType.INSURANCE,
      label: t('singleSession.insurance', 'Insurance'),
    },
    {value: FilterPaymentType.CASH, label: t('singleSession.cash', 'Cash')},
  ];

  const insuranceCompanies = useMemo(() => {
    if (
      !user ||
      user.role === 'patient' ||
      !('acceptedInsurance' in user) ||
      !user.acceptedInsurance ||
      !Array.isArray(user.acceptedInsurance)
    ) {
      return [];
    }
    return user.acceptedInsurance.map((company: string) => ({
      value: company,
      label: company,
    }));
  }, [user]);

  const selectedMemberType = useMemo(() => {
    return memberTypes.find(type => type.value === filters.memberType) || null;
  }, [memberTypes, filters.memberType]);

  const selectedPaymentType = useMemo(() => {
    return (
      paymentTypes.find(type => type.value === filters.paymentType) || null
    );
  }, [paymentTypes, filters.paymentType]);

  const selectedInsuranceCompanies = useMemo(() => {
    return insuranceCompanies.filter(company =>
      filters.insuranceCompany.includes(company.value),
    );
  }, [insuranceCompanies, filters.insuranceCompany]);

  const isSortByLatest = filters.sortBy === 'latest';

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">
          {t('singleSession.filters', 'Filters')}
        </h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {t('singleSession.clearFilters', 'Clear All Filters')}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <SingleComboBox
          options={memberTypes}
          label={t('singleSession.member_type', 'Member Type')}
          onChange={handleMemberTypeChange}
          value={selectedMemberType}
          placeholder={t(
            'singleSession.placeholder.select_an_option',
            'Select an option',
          )}
        />
        <ComboBoxDateRange
          key={`date-range-${filters.dateRange.from}-${filters.dateRange.to}`}
          label={t('singleSession.date_range', 'Date Range')}
          initialRange={{
            startDate: filters.dateRange.from || null,
            endDate: filters.dateRange.to || null,
          }}
          initialPreset="custom"
          onChange={handleDayClick}
        />
        <SingleComboBox
          options={paymentTypes}
          label={t('singleSession.payment_type', 'Payment Type')}
          onChange={handlePaymentTypeChange}
          value={selectedPaymentType}
          placeholder={t(
            'singleSession.placeholder.select_an_option',
            'Select an option',
          )}
        />
        <IndeterminateComboBox
          label={t('singleSession.insurance_company', 'Insurance Company')}
          options={insuranceCompanies}
          onChange={handleInsuranceCompanyChange}
          value={selectedInsuranceCompanies}
          selectedAllLabel={t(
            'singleSession.all_insurance_companies',
            'All Insurance Companies',
          )}
          partlySelectedLabel={t(
            'singleSession.company_selected',
            'Companies Selected',
          )}
          placeholder={t(
            'singleSession.placeholder.select_options',
            'Select options',
          )}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="border flex items-center rounded-2xl overflow-hidden px-4 py-2 relative w-full">
          <FormCustomInput
            id="search-sessions"
            name="search"
            placeholder={t('singleSession.search_sessions', 'Search sessions')}
            className="font-inter text-base text-gray-700 placeholder:text-darkGray p-2 focus:shadow-none border-none"
            bgColor="bg-none"
            value={searchInputValue}
            onChange={handleSearch}
          />
          <SearchIcon height={17} width={17} fillColor="text-black" />
        </div>
        <div className="hidden xl:block" />
        <div className="hidden xl:block" />
        <div className="flex items-center justify-end">
          <button
            className={classNames(
              'flex flex-row justify-center items-center gap-2 font-inter text-base font-medium px-6 py-2.5 rounded-[30px] bg-white border hover:bg-blue-600 hover:text-white w-full sm:max-w-[16rem]',
            )}
            onClick={handleSortBy}
          >
            <ArrowDownShortIcon
              classes={classNames({
                'rotate-180': !isSortByLatest,
              })}
            />
            {isSortByLatest
              ? t('singleSession.sort_by_latest_first', 'Sort by latest first')
              : t('singleSession.sort_by_oldest_first', 'Sort by oldest first')}
          </button>
        </div>
      </div>
    </div>
  );
};
